<template>
  <div
    @click="emit('open', task)"
    :style="tintStyle"
    class="task-card bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg px-3.5 py-3.5 sm:px-3 sm:py-3 cursor-grab active:cursor-grabbing active:scale-[0.98] sm:hover:border-gray-200 dark:sm:hover:border-gray-600 sm:hover:shadow-sm transition-all"
  >
    <!-- Top meta row: status dot + recurring icon, relative age on the right -->
    <div class="flex items-center justify-between gap-2 mb-2">
      <div class="flex items-center gap-1.5 min-w-0">
        <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ backgroundColor: task.color_css_code || '#9ca3af' }"></span>
        <svg v-if="task.is_recurring" class="w-3 h-3 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
        </svg>
      </div>
      <span class="text-[11px] text-gray-400 dark:text-gray-500 flex-shrink-0">{{ age }}</span>
    </div>

    <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-snug" :class="task.status === 'completed' ? 'line-through text-gray-400 dark:text-gray-500' : ''">
      {{ task.title }}
    </p>
    <p v-if="descriptionPreview" class="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">{{ descriptionPreview }}</p>

    <!-- Bottom row: priority pill (mirrors an assignee-avatar pill) + due badge -->
    <div class="flex items-center gap-1.5 mt-3">
      <span
        class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0"
        :class="priority.pill"
        :title="`${priority.label} priority`"
      >{{ priority.label[0] }}</span>
      <span
        v-if="dueLabel || task.is_overdue"
        class="text-[11px] font-medium px-1.5 py-0.5 rounded"
        :class="task.is_overdue ? 'bg-red-50 dark:bg-red-900/20 text-red-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500'"
      >{{ task.is_overdue ? 'Overdue' : dueLabel }}</span>

      <!-- Carried over. A labelled tag here rather than the bare icon the
           checklist row uses: a card has the width for it, and the board is
           grouped by status rather than by date, so nothing else on the card
           hints at why this task keeps turning up in Today. Indigo is its own
           colour across the app — not the gray of the recurring icon above,
           not the blue of the public-link icon beside it. -->
      <span
        v-if="carriesOverNow"
        class="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/25 text-indigo-600 dark:text-indigo-300 flex-shrink-0"
        title="Carries over to Today until it is done"
      >
        <svg class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"/>
        </svg>
        Carry over
      </span>

      <span
        v-if="task.shared_publicly"
        class="ml-auto flex items-center justify-center text-blue-500 dark:text-blue-400 flex-shrink-0"
        title="This task has a public link"
        aria-label="Shared publicly"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
        </svg>
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useTaskDates } from 'composables/useTaskDates.js'
import { priorityMeta } from './taskPriorities.js'
import { taskTintVars } from './taskColors.js'
import { carriesOver } from './taskCarryOver.js'
import { stripHtml } from './stripHtml.js'

const props = defineProps({ task: { type: Object, required: true } })
const emit = defineEmits(['open'])

// The same wash the checklist's rows carry (TaskRow.vue), so a task looks like
// itself in whichever view you happen to be in. The dot in the card header
// already states the color exactly; this makes the whole card scannable at a
// glance without having to find the dot first.
const tintStyle = computed(() => taskTintVars(props.task.color_css_code))

// Only while the flag is actually in effect — see carriesOver().
const carriesOverNow = computed(() => carriesOver(props.task))

const { formatDueDateLabel } = useTaskDates()
const dueLabel = computed(() => formatDueDateLabel(props.task.due_at))
const priority = computed(() => priorityMeta(props.task.priority))
const descriptionPreview = computed(() => stripHtml(props.task.description))

// Compact "9h" / "6d" style age, matching the reference board's card
// header — how long ago the task was created, not due-date-related (that's
// its own badge below).
const age = computed(() => {
  if (!props.task.created_at) return ''
  const diffMs = Date.now() - new Date(props.task.created_at).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 60) return `${Math.max(mins, 1)}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
})
</script>

<style scoped>
/* The card's color wash.

   `background-image`, not `background-color`, and that is the whole trick: the
   card already carries an opaque surface from Tailwind (`bg-white
   dark:bg-[#1C2128]`), and a tint has to sit *on top* of whichever of those
   two is active rather than replace it. Painting a flat gradient composites
   over the surface and leaves the theme's own background-color untouched, so
   there is no specificity contest with `dark:bg-[#1C2128]` and no need to
   restate either surface color in raw CSS. Same technique as `accentWash` in
   TaskDetailPage.vue and `public_task_accent_wash` on the public page.

   The custom properties come from `taskTintVars` — shared with TaskRow.vue, so
   both views tint by the identical amount. */
.task-card {
  background-image: linear-gradient(var(--task-tint), var(--task-tint));
}

/* Hover deepens the same wash. Gated at sm to match the `sm:hover:` variants
   already on this card, so a tap on mobile doesn't leave it stuck looking
   hovered, and excluding the drag classes keeps this from contending with
   TasksBoard.vue's ghost/chosen rules while a card is being dragged. */
@media (min-width: 640px) {
  .task-card:hover:not(.task-drag-ghost, .task-drag-chosen, .task-drag-active) {
    background-image: linear-gradient(var(--task-tint-hover), var(--task-tint-hover));
  }
}
</style>
